import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useNavigate } from "react-router-dom";
import type { PointReason } from "@nifgash/shared/types";
import { Button } from "../components/Button";
import { ErrorBanner } from "../components/ErrorBanner";
import { Loader } from "../components/Loader";
import { tg } from "../lib/telegram";
import { getPoints, type PointsPayload, type PointTransaction } from "../api/points";

const REASON_KEY: Record<PointReason, string> = {
  signup_bonus: "points.reasons.signup_bonus",
  event_attended: "points.reasons.event_attended",
  streak_bonus: "points.reasons.monthly_streak",
  referral_join: "points.reasons.referral_signup",
  referral_active: "points.reasons.referral_attended",
  attendee_verified: "points.reasons.attendee_verified",
  reward_redeem: "points.reasons.reward_redeem",
  manual_adjust: "points.reasons.manual_adjustment",
};

export function Points() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<PointsPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getPoints()
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  useEffect(() => {
    const app = tg();
    app?.MainButton.hide();
    app?.BackButton.show();
    const onBack = () => navigate(-1);
    app?.BackButton.onClick(onBack);
    return () => {
      app?.BackButton.offClick(onBack);
      app?.BackButton.hide();
    };
  }, [navigate]);

  if (error) return <ErrorBanner message={t("points.errorLoad", { error })} />;
  if (!data) return <Loader text={t("points.loading")} full />;

  return (
    <div className="min-h-screen bg-surface-soft pb-12">
      <div
        className="relative px-5 pt-10 pb-8"
        style={{
          background:
            "radial-gradient(ellipse 100% 70% at 50% 0%, var(--color-brand-yellow-soft) 0%, transparent 70%), var(--color-surface)",
        }}
      >
        <div className="text-center">
          <div className="text-[12px] font-bold uppercase tracking-[0.08em] text-ink-500">
            {t("points.yourBalance")}
          </div>
          <div className="text-[56px] font-extrabold text-ink-900 leading-none mt-2 tracking-tight">
            {data.balance}
          </div>
          <div className="text-[14px] text-ink-500 mt-1">
            {t("points.plural", { count: data.balance })}
          </div>
          <div className="text-[13px] text-ink-500 mt-3 max-w-[300px] mx-auto leading-relaxed">
            {t("points.spendHint")}
          </div>
          <div className="mt-4 max-w-[280px] mx-auto">
            <Button stretched size="md" onClick={() => navigate("/rewards")}>
              {t("points.rewardsMarketplace")}
            </Button>
          </div>
        </div>
      </div>

      <div className="px-5 mt-6">
        <SectionLabel>{t("points.howToEarn")}</SectionLabel>
        <div className="space-y-2">
          <EarnRow icon="✅" title={t("points.earnRows.eventAttended")} reward="+15" />
          <EarnRow icon="🎫" title={t("points.earnRows.hostVerified")} reward="+5" />
          <EarnRow icon="👥" title={t("points.earnRows.referralSignup")} reward="+5" />
          <EarnRow icon="🎉" title={t("points.earnRows.referralAttended")} reward="+30" />
          <EarnRow icon="🔥" title={t("points.earnRows.monthlyStreak")} reward="+20" />
        </div>
      </div>

      <div className="px-5 mt-8">
        <SectionLabel>{t("points.history")}</SectionLabel>
        {data.transactions.length === 0 ? (
          <div className="bg-surface rounded-[var(--radius-md)] p-6 border border-ink-100 text-center">
            <div className="text-[14px] text-ink-500">
              {t("points.emptyHistory")}
            </div>
          </div>
        ) : (
          <div className="bg-surface rounded-[var(--radius-md)] border border-ink-100 divide-y divide-ink-100 overflow-hidden">
            {data.transactions.map((tx) => (
              <TransactionRow key={tx.id} tx={tx} t={t} lang={i18n.language} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[12px] font-bold uppercase tracking-[0.08em] text-ink-400 mb-3">
      {children}
    </div>
  );
}

function EarnRow({ icon, title, reward }: { icon: string; title: string; reward: string }) {
  return (
    <div className="bg-surface rounded-[var(--radius-md)] p-3 border border-ink-100 flex items-center gap-3">
      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-brand-yellow-soft flex items-center justify-center text-[18px]">
        {icon}
      </div>
      <div className="flex-1 text-[14px] text-ink-700 font-medium">{title}</div>
      <div className="text-[14px] font-bold text-brand-green flex-shrink-0">{reward}</div>
    </div>
  );
}

function TransactionRow({ tx, t, lang }: { tx: PointTransaction; t: TFunction; lang: string }) {
  const positive = tx.amount > 0;
  return (
    <div className="p-3 flex items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-semibold text-ink-900 truncate">
          {t(REASON_KEY[tx.reason])}
        </div>
        <div className="text-[12px] text-ink-500 mt-0.5">{formatDate(tx.created_at, t, lang)}</div>
      </div>
      <div className={`text-[15px] font-bold flex-shrink-0 ${positive ? "text-brand-green" : "text-brand-red"}`}>
        {positive ? "+" : ""}{tx.amount}
      </div>
    </div>
  );
}

function formatDate(iso: string, t: TFunction, lang: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const time = d.toLocaleTimeString(lang, { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return t("points.todayAt", { time });
  if (isYesterday) return t("points.yesterdayAt", { time });
  return d.toLocaleDateString(lang, { day: "numeric", month: "short" }) + " · " + time;
}
